// Operation search shared by Operations (catalog) and ⌘K.
//
// - the query is split into words; words match in any order;
// - `_`, `.`, `-` and whitespace are one separator ("add_option" = "add option");
// - singular and plural match ("option" ↔ "options", "policy" ↔ "policies");
// - a word matches a token by prefix ("opt" finds "options");
// - fields: operation id, summary, domain (the id's first segment);
// - ranking: every word matched beats a partial match; a match in the id beats one in
//   the summary; exact tokens beat prefixes; words in query order inside the id win ties.

import type { OpMeta } from '@/catalog/types'

const SEP = /[\s_.\-/]+/

/** Crude English singular — enough to make "options"/"option", "policies"/"policy" meet. */
export function stem(word: string): string {
  if (word.length > 4 && word.endsWith('ies')) return `${word.slice(0, -3)}y`
  if (word.length > 4 && /(sses|shes|ches|xes|uses|ases)$/.test(word)) return word.slice(0, -2)
  // status / alias / bus are singular already
  if (word.length > 3 && word.endsWith('s') && !/(ss|us|as)$/.test(word)) return word.slice(0, -1)
  return word
}

export function tokens(text: string): string[] {
  return text.toLowerCase().split(SEP).filter(Boolean).map(stem)
}

type Hit = 'exact' | 'prefix' | null

function hitIn(word: string, toks: string[]): { hit: Hit; at: number } {
  let prefixAt = -1
  for (let i = 0; i < toks.length; i++) {
    if (toks[i] === word) return { hit: 'exact', at: i }
    if (prefixAt < 0 && toks[i].startsWith(word)) prefixAt = i
  }
  return prefixAt >= 0 ? { hit: 'prefix', at: prefixAt } : { hit: null, at: -1 }
}

/** Score of one operation for an already tokenised query; 0 = no word matched. */
export function scoreOp(op: Pick<OpMeta, 'operation_id' | 'summary'>, words: string[]): number {
  if (words.length === 0) return 0
  const idToks = tokens(op.operation_id)
  const sumToks = tokens(op.summary ?? '')
  let matched = 0
  let score = 0
  let lastAt = -1
  let inOrder = 0
  for (const w of words) {
    const id = hitIn(w, idToks)
    if (id.hit) {
      matched++
      // idToks[0] is the domain: "fields" should list fields.* before analysis.field_usage
      score += (id.hit === 'exact' ? 30 : 20) + (id.at === 0 ? 2 : 0)
      if (id.at > lastAt) inOrder++
      lastAt = id.at
      continue
    }
    const sum = hitIn(w, sumToks)
    if (sum.hit) {
      matched++
      score += sum.hit === 'exact' ? 10 : 6
    }
  }
  if (matched === 0) return 0
  if (matched === words.length) score += 1000
  score += inOrder * 2
  // fewer extra tokens in the id = tighter match
  score -= idToks.length * 0.5
  return score
}

/** Ops matching at least one query word, best first. Empty query → input order. */
export function searchOps<T extends Pick<OpMeta, 'operation_id' | 'summary'>>(ops: T[], query: string): T[] {
  const words = tokens(query)
  if (words.length === 0) return ops
  return ops
    .map((op) => ({ op, s: scoreOp(op, words) }))
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s || a.op.operation_id.localeCompare(b.op.operation_id))
    .map((x) => x.op)
}

/** True when every query word matches the text (same tokenising) — for non-op entries. */
export function matchesAllWords(text: string, query: string): boolean {
  const words = tokens(query)
  if (words.length === 0) return true
  const toks = tokens(text)
  return words.every((w) => hitIn(w, toks).hit !== null)
}
